"use client";

import type * as React from "react";
import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "@/components/ui/atoms/popover";
import { ScrollArea } from "@/components/ui/atoms/scroll-area";
import { cn } from "@/lib/utils";
import { McpToolkitAvatar } from "./toolkit-avatar";

export type McpServerAvatarStackItem = {
  id: string;
  name: string;
  toolkitId?: string;
  selectedCount?: number;
  total?: number;
};

export type McpServerAvatarStackProps = {
  servers: McpServerAvatarStackItem[];
  maxVisible?: number;
  overflowLabel?: string;
  onOpenMcpServer?: (id: string) => void;
} & Omit<React.ComponentProps<"span">, "children">;

export function McpServerAvatarStack({
  servers,
  maxVisible = 5,
  overflowLabel = "more MCP servers",
  onOpenMcpServer,
  className,
  ...rest
}: McpServerAvatarStackProps) {
  if (servers.length === 0) {
    return null;
  }

  const visibleServers = servers.slice(0, maxVisible);
  const overflowServers = servers.slice(maxVisible);
  const overflowCount = overflowServers.length;

  return (
    <span
      {...rest}
      className={cn("inline-flex shrink-0 items-center gap-1", className)}
      data-slot="mcp-server-avatar-stack"
    >
      <span className="inline-flex items-center gap-1">
        {visibleServers.map((server) => (
          <McpToolkitAvatar
            key={server.id}
            name={server.name}
            size="xs"
            title={server.name}
            toolkitId={server.toolkitId}
          />
        ))}
      </span>
      {overflowCount > 0 ? (
        <OverflowServersPopover
          label={overflowLabel}
          onOpenMcpServer={onOpenMcpServer}
          servers={overflowServers}
        />
      ) : null}
    </span>
  );
}

function OverflowServersPopover({
  label,
  servers,
  onOpenMcpServer,
}: {
  label: string;
  servers: McpServerAvatarStackItem[];
  onOpenMcpServer?: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const count = servers.length;
  const serverWord = count === 1 ? "server" : "servers";
  const triggerLabel = `Show ${count} ${label}`;

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        aria-label={triggerLabel}
        className="inline-flex size-[18px] cursor-pointer items-center justify-center rounded-full bg-foreground/8 font-medium text-[9px] text-muted-foreground tabular-nums transition-colors hover:bg-foreground/12 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
        title={triggerLabel}
        type="button"
      >
        +{count}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 gap-0 p-0" sideOffset={6}>
        <PopoverHeader className="px-3 pt-2.5 pb-1.5 font-medium text-foreground text-xs">
          {count} more MCP {serverWord}
        </PopoverHeader>
        <ScrollArea className="max-h-72" orientation="vertical" type="hover">
          <ul className="flex flex-col py-1">
            {servers.map((server) => (
              <li key={server.id}>
                <ServerRow
                  onOpenMcpServer={
                    onOpenMcpServer
                      ? (id) => {
                          setOpen(false);
                          onOpenMcpServer(id);
                        }
                      : undefined
                  }
                  server={server}
                />
              </li>
            ))}
          </ul>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function ServerRow({
  server,
  onOpenMcpServer,
}: {
  server: McpServerAvatarStackItem;
  onOpenMcpServer?: (id: string) => void;
}) {
  const content = (
    <>
      <McpToolkitAvatar
        name={server.name}
        size="sm"
        toolkitId={server.toolkitId}
      />
      <span className="min-w-0 flex-1 truncate font-medium capitalize">
        {server.name}
      </span>
      {server.selectedCount != null && server.total != null ? (
        <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">
          {server.selectedCount === server.total
            ? `${server.total}`
            : `${server.selectedCount}/${server.total}`}
        </span>
      ) : null}
    </>
  );

  if (!onOpenMcpServer) {
    return (
      <div className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs">
        {content}
      </div>
    );
  }

  return (
    <button
      aria-label={`Open ${server.name} tools`}
      className="flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors hover:bg-foreground/[0.06] focus-visible:bg-foreground/[0.06] focus-visible:outline-none"
      onClick={() => onOpenMcpServer(server.id)}
      type="button"
    >
      {content}
    </button>
  );
}
